import { useEffect, useRef, useState } from 'react';
import { TYPE_BEAT_EVENT_ENUM } from '@/shared/config';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import type { EventHrInfoProps } from './eventHrInfo';

/**
 * Drives the HR event info popover, cycling through the events that share one heart-rate point. Tracks the open flag and the selected index in state and a ref, derives the event, its type, its channel and whether it is a beat event, wraps the index at both ends on the arrow buttons, resyncs on defaultIndex changes, reports the selection upward while the tab is active, and closes when the event list changes.
 *
 * @param props - Event list and indexes, active flag, AI channel, default index and the selection callbacks.
 * @returns An object carrying the popover state, the derived event fields and the open, hide and arrow handlers.
 */
const useEventHrInfo = ({
  eventIndexes,
  events,
  isActive,
  aiChannel,
  setSelectedEventIndex,
  defaultIndex,
  callbackSetEventIndex,
}: Pick<
  EventHrInfoProps,
  | 'eventIndexes'
  | 'events'
  | 'isActive'
  | 'aiChannel'
  | 'setSelectedEventIndex'
  | 'defaultIndex'
  | 'callbackSetEventIndex'
>) => {
  const [isOpenPopover, setIsOpenPopover] = useState(false);
  const [eventIndex, setEventIndex] = useState(defaultIndex);
  const eventIndexRef = useRef(defaultIndex);
  const event = events[eventIndex] || events[0];
  const eventType = event.type || event?.oldType;
  const channel = event?.channel || aiChannel;
  const isBeatEvent = !!TYPE_BEAT_EVENT_ENUM[eventType as keyof typeof TYPE_BEAT_EVENT_ENUM];

  /**
   * Applies the popover component's own visibility change to the HR event info popover. Stores the requested flag in local state.
   *
   * @param newOpen - Visibility the popover is asking for.
   * @returns Nothing.
   */
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpenPopover(newOpen);
  };

  /**
   * Closes the HR event info popover. Clears the open flag in local state.
   *
   * @returns Nothing.
   */
  const hidePopover = () => {
    setIsOpenPopover(false);
  };

  //* if is select event 1, click arrow left will select event 3, arrow right will select event 2
  /**
   * Selects the previous event among those sharing the heart-rate point. Decrements the index, wrapping to the last event when it falls below zero, and writes it to both the ref and state.
   *
   * @returns Nothing.
   */
  const onClickLeft = () => {
    const newIndex = eventIndex - 1;
    const newEventIndex = newIndex < 0 ? (events?.length ?? 0) - 1 : newIndex;
    eventIndexRef.current = newEventIndex;
    setEventIndex(newEventIndex);
  };
  /**
   * Selects the next event among those sharing the heart-rate point. Increments the index, wrapping back to zero past the last event, and writes it to both the ref and state.
   *
   * @returns Nothing.
   */
  const onClickRight = () => {
    const newIndex = eventIndex + 1;
    const newEventIndex = newIndex > (events?.length ?? 0) - 1 ? 0 : newIndex;
    eventIndexRef.current = newEventIndex;
    setEventIndex(newEventIndex);
  };

  useEffect(() => {
    eventIndexRef.current = defaultIndex;
    setEventIndex(defaultIndex);
  }, [defaultIndex]);

  useEffect(() => {
    if (isActive && events?.length > 1) {
      setSelectedEventIndex(eventIndexes[eventIndexRef.current] || 0);
      callbackSetEventIndex(eventIndexRef.current || 0);
    }
  }, [eventIndexes, eventIndex, isActive]);

  useUpdateEffect(() => {
    setIsOpenPopover(false);
  }, [events]);

  return {
    isOpenPopover,
    eventIndex,
    event,
    eventType,
    channel,
    isBeatEvent,
    handleOpenChange,
    hidePopover,
    onClickLeft,
    onClickRight,
  };
};

export default useEventHrInfo;
